// src/components/home/Home.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getCurrentNews } from '../../utils/api';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import AdSlot from '../ads/AdSlot';

import '../../assets/css/home.css';

// 👉 Lazy imports (laden erst bei Bedarf)
const Tests = React.lazy(() => import('../shorts/Tests'));
const InFeedAd = React.lazy(() => import('../ads/InFeedAd'));

// Kleiner Helper: rendert Kinder erst, wenn im Viewport (oder kurz davor)
function LazyInView({ children, rootMargin = '300px' }) {
    const [visible, setVisible] = React.useState(false);
    const ref = React.useRef(null);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    io.disconnect();
                }
            },
            { rootMargin }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [rootMargin]);

    return <div ref={ref}>{visible ? children : null}</div>;
}

// --- Helpers: HTML -> Plaintext und sicheres Kürzen ---
const toPlainText = (html) =>
    DOMPurify.sanitize(html ?? '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    const t = String(text);
    if (t.length <= maxLength) return t;
    const cut = t.slice(0, maxLength);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '…';
};

const Home = () => {
    const [news, setNews] = useState([]);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const data = await getCurrentNews();
                setNews(data.news);
            } catch (err) {
                console.error(err);
            }
        };
        fetchNews();
    }, []);

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} um ${hours}:${minutes} Uhr`;
    };

    // Aufteilen in zwei Gruppen
    const topNews = news.slice(0, 5);
    const bottomNews = news.slice(5);

    const pageTitle = 'Startseite | NGCTV – Aktuelle Gaming-News, Technik & mehr';
    const pageDescription = 'NGCTV versorgt dich täglich mit News, Tests und Reviews zu Gaming und Technik. Erfahre alles über PC, Konsolen und Handhelds, neue Spiele, spannende Trailer sowie Trends der Gaming-Szene.';

    return (
        <>
            {/* SEO / Head */}
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`https://ngctv.de`} />
            </Helmet>

            <main>
                <div className='wrapper'>
                    {/* News - spezielles Layout */}
                    <div className='top-news-grid'>
                        {topNews.length > 0 && (
                            <>
                                <Link to={`/news/${topNews[0].site_name}`} viewTransition key={topNews[0].id}>
                                    <div className='featured-news'>
                                        <img
                                            src={`/news/bilder/${topNews[0].image}`}
                                            alt={topNews[0].headline}
                                            title={topNews[0].headline}
                                            // Hero: möglichst früh anzeigen
                                            loading="eager"
                                            decoding="sync"
                                        />
                                        <div className='news-overlay'>
                                            <h2>{topNews[0].headline}</h2>
                                            <p className='author'>{topNews[0].username}</p>
                                        </div>
                                    </div>
                                </Link>
                                <div className='side-news'>
                                    {topNews.slice(1).map((item) => (
                                        <Link to={`/news/${item.site_name}`} viewTransition key={item.id}>
                                            <div className='side-news-item'>
                                                <img
                                                    src={`/news/bilder/${item.image}`}
                                                    alt={item.headline}
                                                    title={item.headline}
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                                <div className='news-overlay'>
                                                    <h3>{item.headline}</h3>
                                                    <p className='author'>{item.username}</p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    <div className='rows'>
                        <div className='row'>
                            <div className='category'>Aktuelle News</div>
                            {/* News - Standard-Layout */}
                            <div className='news regular-news'>
                                {bottomNews.map((newsItem, idx) => {
                                    const preview = truncateText(toPlainText(newsItem.content), 180);
                                    return (
                                        <React.Fragment key={newsItem.id}>
                                            <Link to={`/news/${newsItem.site_name}`} viewTransition>
                                                <div className='news-entry'>
                                                    <img
                                                        src={`/news/bilder/${newsItem.image}`}
                                                        alt={newsItem.headline}
                                                        title={newsItem.headline}
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                    <div>
                                                        <div className='news-date'>{formatDate(newsItem.date)}</div>
                                                        <div className='news-headline'>{newsItem.headline}</div>
                                                        <p>{preview}</p>
                                                    </div>
                                                </div>
                                            </Link>

                                            {/* In-Feed-Anzeige nach jedem 5. Eintrag – lazy + erst bei Sichtbarkeit */}
                                            {(idx + 1) % 5 === 0 && (
                                                <div className="infeed-wrapper" style={{ margin: '16px 0' }}>
                                                    <LazyInView>
                                                        <React.Suspense fallback={null}>
                                                            <InFeedAd />
                                                        </React.Suspense>
                                                    </LazyInView>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>

                        <div className='row'>
                            <React.Suspense fallback={null}>
                                <Tests />
                            </React.Suspense>
                            <AdSlot slot="2119524913" />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Home;
